import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const ARTICLES_PER_PAGE = 6;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(ARTICLES_PER_PAGE);

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredArticles = useMemo(() => {
    if (!slug) return articles;
    return articles.filter((a) => a.categoryId === currentCategory?.id);
  }, [slug, currentCategory]);

  const displayedArticles = filteredArticles.slice(0, visibleCount);
  const hasMore = visibleCount < filteredArticles.length;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + ARTICLES_PER_PAGE);
  };

  const handleCategoryChange = (newSlug: string) => {
    setVisibleCount(ARTICLES_PER_PAGE);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const seoTitle = currentCategory
    ? `${currentCategory.name} - 开元体育 | 专业${currentCategory.name}运动资讯与指导`
    : '运动分类 - 开元体育';
  const seoDesc = currentCategory
    ? `${currentCategory.description} 开元体育为您提供最全面的${currentCategory.name}赛事资讯、技术教学与运动指导。`
    : '浏览开元体育所有运动分类，发现最适合您的运动项目。';

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={`开元体育,${currentCategory?.name || '运动'},体育,赛事,运动健身`}
        canonicalUrl={`https://www.kaiyuansports.com/category/${slug || ''}`}
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: seoTitle,
          description: seoDesc,
          url: `https://www.kaiyuansports.com/category/${slug || ''}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '开元体育',
            url: 'https://www.kaiyuansports.com/',
          },
        }}
      />

      <div className="w-full px-4 md:px-6 lg:px-8 pt-20 pb-4 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={
              currentCategory
                ? [{ label: currentCategory.name }]
                : [{ label: t('breadcrumb.category') }]
            }
          />
        </div>
      </div>

      <section className="w-full px-4 md:px-6 lg:px-8 py-4 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-1">
            {currentCategory ? `${currentCategory.name}` : t('category.all')}
          </h1>
          {currentCategory && (
            <p className="text-sm text-foreground-500 mb-6">{currentCategory.description}</p>
          )}
        </div>
      </section>

      <section className="w-full px-4 md:px-6 lg:px-8 py-4 bg-background-100 sticky top-16 z-30 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide flex-wrap">
            <Link
              to="/category/football"
              onClick={() => handleCategoryChange('football')}
              className={`px-4 py-2 text-xs md:text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                slug === 'football'
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-accent-300/60'
              }`}
            >
              {t('category.all')}
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 text-xs md:text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                  slug === cat.slug
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-accent-300/60'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto">
          {displayedArticles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {displayedArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/80 rounded-xl overflow-hidden hover:border-accent-300/60 transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="relative h-44 md:h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-0.5 text-xs font-medium bg-accent-500 text-background-50 rounded-full">
                          {article.categoryName}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.publishedAt}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {hasMore && (
                <div className="text-center mt-10">
                  <button
                    onClick={handleLoadMore}
                    className="px-8 py-3 text-sm font-medium text-foreground-700 bg-background-100 border border-background-200/70 rounded-full hover:bg-background-200/70 transition-colors duration-200 whitespace-nowrap"
                  >
                    {t('category.loadmore')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-2xl mb-4">
                <i className="ri-inbox-line text-3xl text-foreground-300"></i>
              </div>
              <p className="text-sm text-foreground-400">{t('category.noarticles')}</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}